import { useState } from "react";
import {
  ShieldAlert,
  Shield,
  ShieldCheck,
  AlertTriangle,
  Crosshair,
  Swords,
  Target,
  Gauge as GaugeIcon,
  Copy,
  Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ThreatLevel, ThreatScore } from "@/lib/swarmTypes";
import { Sources } from "./Sources";

/**
 * ThreatPanel - the synthesis-layer threat card.
 *
 * Renders a single fused threat assessment for the analysed competitor:
 *   • a radial gauge for score / 10, coloured + labelled + iconed by level
 *     (colour is never the only signal - every level pairs a colour with an
 *     icon and an UPPERCASE text label for AA-accessible, colour-blind-safe
 *     reading)
 *   • a one-line rationale
 *   • a prominent "Why it matters to US" callout
 *   • animated driver/dimension bars (width transition; the global
 *     prefers-reduced-motion rule in index.css neutralises the animation)
 *   • compact Top Threats / Counter-Moves lists
 *   • a confidence chip and clickable cyan source links
 *
 * While the run is in flight and no threat has arrived yet, a skeleton is shown.
 */

interface ThreatPanelProps {
  threat: ThreatScore | null;
  competitor?: string;
  /** True while the swarm is still running - drives the skeleton state. */
  isRunning?: boolean;
}

interface LevelTheme {
  label: string;
  Icon: LucideIcon;
  /** Stroke / accent hex used by the gauge + glows. */
  hex: string;
  /** Tailwind text class for level label + icon. */
  text: string;
  /** Tailwind chip (bg + border) for the level badge. */
  chip: string;
  /** Bar fill gradient (from→to) for dimension bars. */
  barFrom: string;
}

// CRITICAL red / HIGH amber / MEDIUM cyan / LOW slate - each colour is paired
// with a distinct icon + text label so meaning never relies on colour alone.
const LEVEL_THEME: Record<ThreatLevel, LevelTheme> = {
  CRITICAL: {
    label: "Critical threat",
    Icon: ShieldAlert,
    hex: "#ef4444",
    text: "text-red-400",
    chip: "border-red-700/40 bg-red-500/10 text-red-300",
    barFrom: "from-red-500 to-red-400",
  },
  HIGH: {
    label: "High threat",
    Icon: AlertTriangle,
    hex: "#f59e0b",
    text: "text-amber-400",
    chip: "border-amber-600/40 bg-amber-500/10 text-amber-300",
    barFrom: "from-amber-500 to-amber-400",
  },
  MEDIUM: {
    label: "Medium threat",
    Icon: Shield,
    hex: "#22d3ee",
    text: "text-cyan-300",
    chip: "border-cyan-600/40 bg-cyan-500/10 text-cyan-300",
    barFrom: "from-cyan-500 to-cyan-400",
  },
  LOW: {
    label: "Low threat",
    Icon: ShieldCheck,
    hex: "#94a3b8",
    text: "text-slate-300",
    chip: "border-slate-600/40 bg-white/5 text-slate-300",
    barFrom: "from-slate-500 to-slate-400",
  },
};

export function ThreatPanel({ threat, competitor, isRunning }: ThreatPanelProps) {
  const [copied, setCopied] = useState(false);

  if (!threat) {
    return isRunning ? <ThreatSkeleton /> : null;
  }

  const theme = LEVEL_THEME[threat.level];
  const { Icon } = theme;

  async function copySummary() {
    if (!threat) return;
    try {
      await navigator.clipboard.writeText(buildSummary(threat));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable - no-op */
    }
  }

  const name = threat.competitor || competitor || "Competitor";

  return (
    <section
      className="ns-glass ns-reveal rounded-2xl p-5 sm:p-6 shadow-card"
      aria-label={`Threat assessment for ${name}: ${theme.label}, score ${threat.score} out of 10`}
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg border ${theme.chip}`}>
            <Icon className={`w-4 h-4 ${theme.text}`} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">
              Threat assessment
            </p>
            <h3 className="font-mono text-base font-bold text-white truncate leading-tight">{name}</h3>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={copySummary}
            title="Copy threat summary"
            aria-label="Copy threat summary to clipboard"
            className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-slate-400 hover:text-[#22d3ee] hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[36px]"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>
      </div>

      {/* ── Gauge + level + rationale ── */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-5">
        <Gauge score={threat.score} theme={theme} />

        <div className="min-w-0 flex-1">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${theme.chip}`}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {theme.label}
          </span>
          {threat.rationale && (
            <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{threat.rationale}</p>
          )}
        </div>
      </div>

      {/* ── Why it matters to US ── */}
      {threat.whyItMattersToUs && (
        <div className="mt-5 rounded-xl border border-[#22d3ee]/25 bg-[#22d3ee]/[0.06] p-4">
          <div className="flex items-center gap-2">
            <Crosshair className="w-4 h-4 text-[#22d3ee] shrink-0" aria-hidden="true" />
            <p className="text-[11px] font-semibold text-[#22d3ee] uppercase tracking-[0.18em]">
              Why it matters to us
            </p>
          </div>
          <p className="mt-2 text-sm text-slate-200 leading-relaxed">{threat.whyItMattersToUs}</p>
        </div>
      )}

      {/* ── Dimension / driver bars ── */}
      {threat.dimensions.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-3">
            <GaugeIcon className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em]">
              Threat drivers
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {threat.dimensions.map((d) => (
              <DimensionBar key={d.key} label={d.label} score={d.score} max={d.max} note={d.note} theme={theme} />
            ))}
          </div>
        </div>
      )}

      {/* ── Top threats / Counter-moves ── */}
      {(threat.topThreats.length > 0 || threat.counterMoves.length > 0) && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {threat.topThreats.length > 0 && (
            <SignalList
              title="Top threats"
              items={threat.topThreats}
              Icon={Swords}
              iconClass="text-amber-400"
              dotClass="bg-amber-400/70"
            />
          )}
          {threat.counterMoves.length > 0 && (
            <SignalList
              title="Counter-moves"
              items={threat.counterMoves}
              Icon={Target}
              iconClass="text-emerald-400"
              dotClass="bg-emerald-400/70"
            />
          )}
        </div>
      )}

      {/* ── Sources ── */}
      {threat.sources.length > 0 && (
        <Sources urls={threat.sources} className="mt-5 pt-4 border-t border-white/10" />
      )}

      {/* ── Confidence - calm, quiet secondary metadata at the foot of the card ── */}
      <div className="mt-4 flex justify-end">
        <ConfidenceChip value={threat.confidence} />
      </div>
    </section>
  );
}

// ── Radial gauge (semicircular arc, score / 10) ──────────────────────────────

function Gauge({ score, theme }: { score: number; theme: LevelTheme }) {
  // Semicircle arc from 180° → 360°. Radius/stroke tuned for a compact card.
  const size = 140;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const semi = Math.PI * r; // arc length of a half circle
  const pct = Math.min(1, Math.max(0, score / 10));

  // Build the half-circle path (left → right along the top).
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  return (
    <div className="relative shrink-0 mx-auto sm:mx-0" style={{ width: size, height: size / 2 + 28 }}>
      <svg width={size} height={size / 2 + 8} viewBox={`0 0 ${size} ${size / 2 + 8}`} aria-hidden="true">
        {/* Track */}
        <path d={arc} fill="none" stroke="#1a2640" strokeWidth={stroke} strokeLinecap="round" />
        {/* Value arc - width transition is reduced-motion-safe via global CSS */}
        <path
          d={arc}
          fill="none"
          stroke={theme.hex}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={semi}
          strokeDashoffset={semi * (1 - pct)}
          style={{
            transition: "stroke-dashoffset 900ms cubic-bezier(0.16,1,0.3,1)",
            filter: `drop-shadow(0 0 5px ${theme.hex}66)`,
          }}
        />
      </svg>
      {/* Numeric readout */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
        <span className={`font-mono text-3xl font-bold leading-none ${theme.text}`}>{score}</span>
        <span className="mt-0.5 text-[10px] font-mono text-slate-500 tracking-wider">/ 10</span>
      </div>
    </div>
  );
}

// ── Dimension bar ────────────────────────────────────────────────────────────

function DimensionBar({
  label,
  score,
  max,
  note,
  theme,
}: {
  label: string;
  score: number;
  max: number;
  note: string;
  theme: LevelTheme;
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (score / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-slate-300 truncate">{label}</span>
        <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">
          {score}
          <span className="text-slate-600">/{max}</span>
        </span>
      </div>
      <div
        className="h-2 rounded-full bg-[#0a1322] border border-[#1a2640]/60 overflow-hidden"
        role="meter"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={`${label}: ${score} of ${max}`}
      >
        <div
          className={`h-full rounded-full bg-gradient-to-r ${theme.barFrom}`}
          // width transition disabled under prefers-reduced-motion by the global rule
          style={{ width: `${pct}%`, transition: "width 800ms cubic-bezier(0.16,1,0.3,1)" }}
        />
      </div>
      {note && <p className="mt-1 text-[11px] text-slate-500 leading-snug">{note}</p>}
    </div>
  );
}

// ── Signal list (top threats / counter-moves) ────────────────────────────────

function SignalList({
  title,
  items,
  Icon,
  iconClass,
  dotClass,
}: {
  title: string;
  items: string[];
  Icon: LucideIcon;
  iconClass: string;
  dotClass: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
        <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">{title}</p>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
            <span className={`mt-1.5 w-1 h-1 rounded-full shrink-0 ${dotClass}`} aria-hidden="true" />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Confidence chip ──────────────────────────────────────────────────────────

function ConfidenceChip({ value }: { value: number }) {
  // Calm, quiet secondary metadata - intentionally low-contrast so it reads as a
  // footnote, not a headline.
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-600"
      title="Model confidence in this assessment"
    >
      <GaugeIcon className="w-3 h-3 text-slate-700" aria-hidden="true" />
      {value}% confidence
    </span>
  );
}

// ── Skeleton (while running, before threat arrives) ──────────────────────────

function ThreatSkeleton() {
  return (
    <section
      className="ns-glass rounded-2xl p-5 sm:p-6 shadow-card"
      role="status"
      aria-label="Scoring threat level"
    >
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-lg bg-white/5 animate-pulse" />
        <div className="space-y-1.5">
          <div className="h-2.5 w-28 rounded bg-white/5 animate-pulse" />
          <div className="h-3.5 w-40 rounded bg-white/10 animate-pulse" />
        </div>
      </div>
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="h-[84px] w-[140px] rounded-full bg-white/5 animate-pulse mx-auto sm:mx-0" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-32 rounded-full bg-white/10 animate-pulse" />
          <div className="h-2.5 w-full rounded bg-white/5 animate-pulse" />
          <div className="h-2.5 w-4/5 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {[92, 78, 85].map((w, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-2.5 w-24 rounded bg-white/5 animate-pulse" />
            <div className="h-2 rounded-full bg-white/5 animate-pulse" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-slate-500 font-mono">Fusing signals into a threat score…</p>
    </section>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildSummary(t: ThreatScore): string {
  const lines: string[] = [];
  const name = t.competitor || "Competitor";
  lines.push(`Threat assessment - ${name}`);
  lines.push(`Level: ${t.level} (${t.score}/10, ${t.confidence}% confidence)`);
  if (t.rationale) lines.push(`Rationale: ${t.rationale}`);
  if (t.whyItMattersToUs) lines.push(`Why it matters to us: ${t.whyItMattersToUs}`);
  if (t.dimensions.length) {
    lines.push("", "Threat drivers:");
    for (const d of t.dimensions) {
      lines.push(`- ${d.label}: ${d.score}/${d.max}${d.note ? ` - ${d.note}` : ""}`);
    }
  }
  if (t.topThreats.length) {
    lines.push("", "Top threats:");
    for (const x of t.topThreats) lines.push(`- ${x}`);
  }
  if (t.counterMoves.length) {
    lines.push("", "Counter-moves:");
    for (const x of t.counterMoves) lines.push(`- ${x}`);
  }
  if (t.sources.length) {
    lines.push("", "Sources:");
    for (const s of t.sources) lines.push(`- ${s}`);
  }
  return lines.join("\n");
}
